import { theme } from '@notino/react-styleguide'
import styled from 'styled-components'
import { BREAKPOINTS } from '../../styles/constants'
import { NotinoLabelRegular400 } from '../../styles/helpers'

type ColorMapping = {
	key: 'blue' | 'purple' | 'gray' | 'yellow' | 'pink' | 'red' | 'green'
	value: string
}

const COLOR_MAPPINGS: ColorMapping[] = [
	{ key: 'blue', value: theme.color.text.primary },
	{ key: 'purple', value: theme.color.text.secondary },
	{ key: 'gray', value: theme.color.text.tertiary },
	{ key: 'yellow', value: theme.color.text.disabled },
	{ key: 'pink', value: theme.color.text.highlight },
	{ key: 'red', value: theme.color.text.negative },
	{ key: 'green', value: theme.color.text.positive }
] as const

/**
 * Generates CSS rules for text and background colors
 * @param color - Color mapping entry
 */
const generateColorRules = ({ key, value }: ColorMapping): string => `
    [data-text-color='${key}'] {
        color: ${value};
    }
    [data-background-color='${key}'] {
        background-color: ${value};
    }
`

const RichTextWrapper = styled.div`
	min-width: 0;
	${COLOR_MAPPINGS.map(generateColorRules).join('')}
	${NotinoLabelRegular400};
	// TODO: check sizes for headings
	h1 {
		font-size: 2rem;
		line-height: 2.25rem;
		font-weight: 500;
	}
	h2 {
		font-size: 1.5rem;
		line-height: 2rem;
		font-weight: 500;
	}
	h3 {
		font-size: 1.25rem;
		line-height: 1.5rem;
		font-weight: 500;
	}
	p {
		margin: 0 0 1rem 0;
	}

	p:empty {
		margin-bottom: 1rem;
	}

	ul,
	ol {
		list-style-position: inside;
	}

	ul {
		list-style-type: disc;
	}

	ol {
		list-style-type: decimal;
	}

	li {
		margin-bottom: 0.5rem;
	}

	li:last-child {
		margin-bottom: 0;
	}

	[data-text-alignment='left'] {
		text-align: left;
	}

	[data-text-alignment='center'] {
		text-align: center;
	}

	[data-text-alignment='right'] {
		text-align: right;
	}

	a {
		color: ${theme.color.common.black};
		text-decoration: underline;

		&:hover {
			text-decoration: underline;
		}
	}

	.rich-text-table-scroll {
		max-width: 100%;
		min-width: 0;
		container-type: inline-size;
		--column-min-width: 15rem;
		--column-gap: 2rem;
		--table-content-min-width: calc(var(--column-count) * var(--column-min-width) + (var(--column-count) - 1) * var(--column-gap));

		.rich-text-table-wrapper {
			width: 100%;
			overflow: hidden;
		}

		table {
			width: calc(100% + 4rem);
			table-layout: fixed;
			border-collapse: separate;
			border-spacing: 2rem 0;
			margin: 0 -2rem;
		}

		@media (max-width: ${BREAKPOINTS.xl}) {
			overflow-x: auto;
			-webkit-overflow-scrolling: touch;

			.rich-text-table-wrapper {
				width: max(100cqi, var(--table-content-min-width));
				min-width: 100cqi;
				overflow: hidden;
			}

			table {
				min-width: calc(max(100cqi, var(--table-content-min-width)) + 4rem);
			}
		}
	}

	.rich-text-table-scroll th,
	.rich-text-table-scroll td {
		${theme.typography.labelRegular};
		font-family: ${theme.fonts.primary};
		border: none;
		padding: 0.75rem 0;
		vertical-align: top;
		overflow-wrap: break-word;

		@media (max-width: ${BREAKPOINTS.xl}) {
			min-width: var(--column-min-width);
		}
	}

	.rich-text-table-scroll thead th {
		${theme.typography.labelSmall};
		color: ${theme.color.text.secondary};
		border-bottom: 1px solid ${theme.color.border.divider};
	}

	.rich-text-table-scroll a {
		${theme.typography.labelRegular};
		font-family: ${theme.fonts.primary};
		color: ${theme.color.text.primary};
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}
`

export default RichTextWrapper
